import React, { useEffect, useMemo, useState } from "react";
import { Modal, Table, Button } from "react-bootstrap";
import { msmartAxios } from "../../api/axios";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { getErrorMessage } from "../../utils/errorMessage";

export default function ViewSubmissionsModal({ show, onHide, formId, token, teamId }) {
  const [submissions, setSubmissions] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    const run = async () => {
      if (!show || !formId) return;
      setLoading(true);
      try {
        const res = await msmartAxios.get(`/api/msmart/lead-form/${formId}/submissions`, {
          headers: { accessToken: token },
        });
        setSubmissions(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to fetch submissions"));
        setSubmissions([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [show, formId, token]);

  // --- CSV helpers ---
  const csvEscape = (val) => {
    if (val === null || val === undefined) return "";
    const s = typeof val === "object" ? JSON.stringify(val) : String(val);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const handleExportCsv = () => {
    if (!submissions?.length) {
      toast.error("No submissions to export");
      return;
    }

    const answerObjs = submissions.map((s) => s.answers || {});
    const keySet = new Set();
    answerObjs.forEach((a) => Object.keys(a).forEach((k) => keySet.add(k)));

    const preferred = ["countryCode", "phone", "name"];
    const others = [...keySet].filter((k) => !preferred.includes(k)).sort();
    const headers = [...preferred.filter((k) => keySet.has(k)), ...others];

    if (headers.length === 0) {
      toast.error("Submissions have no answer fields to export");
      return;
    }

    const rows = answerObjs.map((a) => headers.map((h) => csvEscape(a[h])));
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `form-${formId || "unknown"}-submissions.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("CSV exported");
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return submissions;

    return submissions.filter((s) => {
      const ans = s.answers || {};
      const phone = `${ans.countryCode || ""}${ans.phone || ""}`.toLowerCase();
      const name = String(ans.name || "").toLowerCase();
      const status = String(s?.msmartlead?.status || "No Status").toLowerCase();
      return phone.includes(query) || name.includes(query) || status.includes(query);
    });
  }, [submissions, q]);

  const formatKL = (dt) => {
    if (!dt) return "-";
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Kuala_Lumpur",
      hour12: true,
    });
  };

  const statusBadge = (status) => {
    const s = String(status || "No Status");
    const lower = s.toLowerCase();
    if (lower === "closed") return "bg-success";
    if (lower === "booking") return "bg-warning text-dark";
    if (lower === "rejected") return "bg-danger";
    if (lower === "follow up") return "bg-info text-dark";
    return "bg-secondary";
  };

  return (
    <div className="ss-page">
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton className="ss-modal-header">
          <Modal.Title>Form Submissions</Modal.Title>
        </Modal.Header>

        <Modal.Body className="ss-modal-body">
          {/* Toolbar */}
          <div className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center mb-3">
            <div className="form-floating flex-grow-1">
              <input
                className="form-control"
                id="subSearch"
                placeholder="Search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <label htmlFor="subSearch">Search (phone / name / status)</label>
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <Button variant="outline-secondary" size="sm" onClick={handleExportCsv}>
                <i className="bi bi-download me-1"></i> Export CSV
              </Button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="mt-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="skeleton-box mb-2" style={{ height: 34, borderRadius: 10 }} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-muted mb-0">No submissions found.</p>
          ) : (
            <Table hover responsive className="mb-0" style={{ fontSize: "0.9rem" }}>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>Phone</th>
                  <th>Name</th>
                  <th style={{ width: 120 }}>Status</th>
                  <th style={{ width: 140 }} className="text-end">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((sub, i) => {
                  const ans = sub.answers || {};
                  const lead = sub.msmartlead || {};
                  const isExpanded = expandedRowId === sub.id;

                  const extraFields = Object.entries(ans)
                    .filter(([key]) => !["name", "phone", "countryCode"].includes(key))
                    .map(([key, value]) => (
                      <div key={key} className="mb-1">
                        <span className="text-muted">{key}:</span>{" "}
                        <span className="fw-semibold">
                          {typeof value === "object" ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    ));

                  const phone = `${ans.countryCode || ""}${ans.phone || ""}` || "-";

                  return (
                    <React.Fragment key={sub.id}>
                      <tr
                        onClick={() => setExpandedRowId(isExpanded ? null : sub.id)}
                        style={{ cursor: "pointer" }}
                        className="align-middle"
                      >
                        <td>{i + 1}</td>
                        <td className="fw-semibold">{phone}</td>
                        <td>{ans.name || "-"}</td>
                        <td>
                          <span className={`badge rounded-pill ${statusBadge(lead.status)}`}>
                            {lead.status || "No Status"}
                          </span>
                        </td>
                        <td className="text-end">
                          <span className="text-muted">
                            {isExpanded ? "Collapse ↑" : "Expand ↓"}
                          </span>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-light">
                          <td colSpan={5}>
                            <div className="p-3 rounded border" style={{ background: "rgba(255,255,255,0.9)" }}>
                              <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                                <div>
                                  <h6 className="mb-2">Answers</h6>
                                  {extraFields.length ? (
                                    <div>{extraFields}</div>
                                  ) : (
                                    <em className="text-muted">No extra fields submitted.</em>
                                  )}

                                  <div className="mt-3 text-muted" style={{ fontSize: "0.85rem" }}>
                                    <div>
                                      <strong>Submitted At:</strong> {formatKL(sub.submittedAt)}{" "}
                                      {sub.desc?.timezone ? `(${sub.desc.timezone})` : ""}
                                    </div>
                                  </div>
                                </div>

                                <div className="text-md-end">
                                  <Link
                                    to={`/msmart/db/manage/${teamId}?searchPhone=${phone}`}
                                    className="btn btn-outline-primary btn-sm"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <i className="bi bi-box-arrow-up-right me-1"></i>
                                    Manage Lead
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
